import { useState } from 'react';
import { Card, Button, Alert, Spinner } from 'react-bootstrap';
import { RichText } from '@common/RichText';
import './RemediationPanel.css';

/**
 * RemediationPanel Component
 * Displays remedial content when a user struggles with a topic (3+ consecutive incorrect answers)
 *
 * Receives content from the API via the section prop:
 *   section: { id, title, contentText, examples }
 *   context: { tagId, consecutiveErrors }
 */
export function RemediationPanel({
  section = null,
  context = null,
  message = null,
  onComplete = null,
  loading = false,
}) {
  const [acknowledged, setAcknowledged] = useState(false);

  const handleAcknowledge = async () => {
    setAcknowledged(true);
    setTimeout(() => {
      onComplete?.();
    }, 300);
  };

  if (!section) {
    return (
      <Card className="text-center">
        <Card.Body className="py-5">
          <Alert variant="warning">Remediation content not available.</Alert>
          <Button variant="secondary" onClick={() => onComplete?.()}>Continue</Button>
        </Card.Body>
      </Card>
    );
  }

  const examples = section.examples || [];

  return (
    <div className={`remediation-panel ${acknowledged ? 'acknowledged' : ''}`}>
      {/* Header */}
      <div className="remediation-header">
        <div className="remediation-icon">💡</div>
        <h3>Refresh Your Memory</h3>
        <p className="remediation-subtitle">
          {message || `Let's review "${section.title}" before continuing`}
        </p>
      </div>

      {/* Remediation context badge */}
      {context?.consecutiveErrors > 0 && (
        <Alert variant="warning" className="mb-3">
          <strong>⚠️ {context.consecutiveErrors} consecutive errors</strong> on this topic.
          Let&apos;s review the material to help you master it.
        </Alert>
      )}

      {/* Main content text */}
      {section.contentText && (
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-light border-bottom">
            <h5 className="mb-0">{section.title}</h5>
          </Card.Header>
          <Card.Body style={{ maxHeight: '400px', overflowY: 'auto', backgroundColor: '#fafafa' }}>
            <div
              className="content-text"
              dangerouslySetInnerHTML={{ __html: section.contentText }}
              style={{ lineHeight: '1.8', fontSize: '1rem' }}
            />
          </Card.Body>
        </Card>
      )}

      {/* Examples */}
      {examples.length > 0 && (
        <div className="remediation-content mb-4">
          {examples.map((example, index) => (
            <div key={example.id || index} className="remediation-block mb-3">
              {example.title && (
                <h6 className="fw-bold">{example.title}</h6>
              )}
              {/* Text content */}
              {example.content && example.contentType !== 'code' && (
                <div
                  className="remediation-text"
                  dangerouslySetInnerHTML={{ __html: example.content }}
                  style={{ lineHeight: '1.7' }}
                />
              )}
              {/* Code content */}
              {(example.code || (example.content && example.contentType === 'code')) && (
                <pre className="remediation-code bg-dark text-light p-3 rounded">
                  <code>{example.code || example.content}</code>
                </pre>
              )}
              {/* Legacy explanation */}
              {example.explanation && (
                <div className="mt-2 text-muted small">
                  <strong>Explanation:</strong> <RichText value={example.explanation} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Action button */}
      <div className="remediation-actions d-grid gap-2">
        <Button
          variant="primary"
          size="lg"
          onClick={handleAcknowledge}
          disabled={acknowledged || loading}
          className="py-3"
        >
          {loading || acknowledged ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" role="status" aria-hidden="true" />
              {acknowledged ? 'Continuing...' : 'Loading...'}
            </>
          ) : (
            "I've Reviewed This — Continue"
          )}
        </Button>
      </div>

      {acknowledged && (
        <div className="remediation-confirmed text-center mt-3">
          <span className="check-mark text-success fw-bold">✓ </span>
          <span className="text-muted">Let&apos;s try some questions on this topic!</span>
        </div>
      )}
    </div>
  );
}

export default RemediationPanel;
